import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import Alert from '../../Alert';
import Section from '../../Section';
import Modal from '../../Modal';
import useDebouncedAutosave from './useDebouncedAutosave';
import './ProductCompliancePage.css';

const DEFAULT_DOCUMENT_TYPES = [
    { value: 'pre_scene_consent_video', label: 'Pre-scene consent video' },
    { value: 'post_scene_consent_video', label: 'Post-scene consent video' },
    { value: 'scene_rider', label: 'Signed scene rider' },
    { value: 'safeword_statement', label: 'Safeword or safety-protocol note' },
    { value: 'rack_ssc_waiver', label: 'RACK or SSC waiver' },
    { value: 'sti_test', label: 'PASS STI test' },
    { value: 'performer_request', label: 'Explicit performer request video' },
    { value: 'safety_monitor', label: 'Safety monitor attestation' },
    { value: 'set_safety_protocol', label: 'Set safety protocol' },
    { value: 'location_release', label: 'Location release' },
    { value: 'filming_permit', label: 'Filming permit' },
    { value: 'bystander_review', label: 'Bystander/privacy review note' },
    { value: 'weapon_documentation', label: 'Weapon or FFL documentation' },
    { value: 'weapon_safety_protocol', label: 'Weapon safety protocol' },
    { value: 'other', label: 'Other supporting document' },
];

const DEFAULT_RESTRICTED_TYPES = [
    {
        value: 'bdsm',
        label: 'Impact / BDSM',
        description: 'Bondage, discipline, dominance-submission, sadomasochism, and impact play need extra consent and set-safety documentation.',
        requirements: ['On-camera consent checks', 'RACK or SSC waiver', 'PASS STI tests for penetrative scenes', 'Quick-release restraints and safety shears on set'],
        documentation: ['On-camera consent check', 'RACK or SSC waiver', 'PASS STI test, when applicable', 'Set safety protocol'],
    },
    {
        value: 'cnc',
        label: 'CNC / forced role-play',
        description: 'Consensual non-consent or forced-roleplay themes are allowed only with explicit scene-specific consent and a clear safety plan.',
        requirements: ['Uncut pre-scene consent video from every performer', 'Uncut post-scene consent video from every performer', 'Signed CNC scene rider with a safeword clause', 'Safeword stated on camera'],
        documentation: ['Pre-scene consent video', 'Post-scene consent video', 'Signed CNC scene rider', 'Safeword or safety-protocol note'],
    },
    {
        value: 'rough',
        label: 'Rough sex',
        description: 'Hair-pulling, slapping, choking, and other rough-sex depictions need explicit performer request and safety documentation.',
        requirements: ['Same safeguards as BDSM content', 'Explicit performer request recorded on camera', 'Trained safety monitor present on set'],
        documentation: ['On-camera consent check', 'Explicit performer request video', 'Safety monitor attestation'],
    },
    {
        value: 'breathplay',
        label: 'Breath play',
        description: 'Breath play is treated as rough-sex content and needs clear affirmative consent plus safety-monitor documentation.',
        requirements: ['On-camera consent checks', 'Explicit performer request recorded on camera', 'Trained safety monitor present on set', 'Set safety protocol covering breath-play limits'],
        documentation: ['On-camera consent check', 'Explicit performer request video', 'Safety monitor attestation', 'Breath-play safety protocol'],
    },
    {
        value: 'public',
        label: 'Public or semi-public scene',
        description: 'Public or semi-public scenes must be legal, controlled, and free of uninformed bystanders.',
        requirements: ['Location release and/or municipal filming permit', 'No uninformed bystanders visible', 'Compliance with local exposure laws'],
        documentation: ['Location release or filming permit', 'Bystander/privacy review note'],
    },
    {
        value: 'firearms',
        label: 'Firearm / weapon role-play',
        description: 'Weapon role-play needs documentation that the weapon was lawful and the set was controlled.',
        requirements: ['Prop or replica weapons, or proof of FFL license', 'Closed-set safety protocol brief recorded', 'No live ammunition on set'],
        documentation: ['Prop, replica, or FFL documentation', 'Closed-set weapon safety protocol', 'No-live-ammunition attestation'],
    },
];

function getErrorMessage(json, fallback) {
    if (json?.missingRequirements?.length) {
        return `${json.error || fallback}: ${json.missingRequirements.join('; ')}`;
    }
    return json?.error || json?.message || fallback;
}

async function readJson(response) {
    const text = await response.text();
    if (!text) return {};
    try {
        return JSON.parse(text);
    } catch (e) {
        return { message: text };
    }
}

function formatDateTime(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
}

function ageAsOf(ageVerification, dateFilmed) {
    if (!ageVerification?.birthdate || !dateFilmed) return null;
    const birthDate = new Date(ageVerification.birthdate);
    const filmedDate = new Date(dateFilmed);
    if (Number.isNaN(birthDate.getTime()) || Number.isNaN(filmedDate.getTime())) return null;
    const ageMs = filmedDate.getTime() - birthDate.getTime();
    return Math.floor(ageMs / (365.25 * 24 * 60 * 60 * 1000));
}

function titleize(value) {
    if (!value) return 'Pending';
    return value.charAt(0).toUpperCase() + value.slice(1);
}

function WarningChecklist({ policy, selectedWarnings, documents, onToggle, onDetails }) {
    const docsCovering = (warning) => documents.filter(doc => (doc.relatedWarnings || []).includes(warning));

    return (
        <div className="pc-warning-list">
            {policy.map(item => {
                const checked = selectedWarnings.includes(item.value);
                const warningDocs = docsCovering(item.value);
                return (
                    <div className={`pc-warning-row ${checked ? 'is-selected' : ''}`} key={item.value}>
                        <label className="checkbox">
                            <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => onToggle(item.value)}
                            />
                            <span>{item.label}</span>
                        </label>
                        <p>{item.description}</p>
                        <div className="pc-warning-actions">
                            {checked && (
                                <span className={warningDocs.length ? 'pc-pill success' : 'pc-pill warning'}>
                                    {warningDocs.length ? `${warningDocs.length} doc${warningDocs.length === 1 ? '' : 's'}` : 'Needs docs'}
                                </span>
                            )}
                            <button type="button" className="button secondary pc-req-btn" onClick={() => onDetails(item)}>
                                Requirements
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function DocumentsSummary({ selectedWarnings, policy, documents }) {
    return (
        <div className="pc-doc-summary">
            <div className="pc-doc-summary-row">
                <div>
                    <strong>Every RED video or scene</strong>
                    <ul>
                        <li>Date and location of production</li>
                        <li>Custodian of records with a physical street or business address</li>
                        <li>One performer record for every person depicted</li>
                        <li>Model release and 2257 records for every performer</li>
                    </ul>
                </div>
                <span className="pc-pill success">Baseline</span>
            </div>
            {!selectedWarnings.length && (
                <p className="help">No restricted content types selected — no additional documentation is required beyond the baseline above.</p>
            )}
            {selectedWarnings.map(warning => {
                const item = policy.find(p => p.value === warning);
                const matchingDocs = documents.filter(doc => (doc.relatedWarnings || []).includes(warning));
                return (
                    <div className="pc-doc-summary-row" key={warning}>
                        <div>
                            <strong>{item?.label || warning}</strong>
                            <ul>
                                {(item?.documentation || []).map(doc => <li key={doc}>{doc}</li>)}
                            </ul>
                        </div>
                        <span className={matchingDocs.length ? 'pc-pill success' : 'pc-pill warning'}>
                            {matchingDocs.length ? 'Provided' : 'Missing'}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

function PerformerTable({ performers, dateFilmed }) {
    const hasRows = performers.productPerformers.length || performers.complianceInvitations.length;

    return (
        <table className="pc-table">
            <thead>
                <tr>
                    <th>Performer</th>
                    <th>Age at filming</th>
                    <th>Status</th>
                </tr>
            </thead>
            <tbody>
                {performers.complianceInvitations.map(invite => (
                    <tr key={`invite-${invite.email}`}>
                        <td data-label="Performer">{invite.email}</td>
                        <td data-label="Age at filming">-</td>
                        <td data-label="Status">Invited</td>
                    </tr>
                ))}
                {performers.productPerformers.map(performer => {
                    const age = ageAsOf(performer.AgeVerification, dateFilmed);
                    return (
                        <tr key={performer.uuid || performer.id}>
                            <td data-label="Performer">{performer.name}</td>
                            <td data-label="Age at filming">{age === null ? '-' : age}</td>
                            <td data-label="Status">{titleize(performer.AgeVerification?.status)}</td>
                        </tr>
                    );
                })}
                {!hasRows && (
                    <tr>
                        <td colSpan={3}>No performers added yet.</td>
                    </tr>
                )}
            </tbody>
        </table>
    );
}

function DocumentUploader({ productUuid, policy, documentTypes, onUploaded }) {
    const { fetch, API_URL } = useAuth();
    const [values, setValues] = useState({
        documentType: 'other',
        label: '',
        notes: '',
        relatedWarnings: [],
    });
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const toggleWarning = (warning) => {
        setValues(prev => ({
            ...prev,
            relatedWarnings: prev.relatedWarnings.includes(warning)
                ? prev.relatedWarnings.filter(item => item !== warning)
                : [...prev.relatedWarnings, warning],
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        if (!file) {
            setError('Choose a document to upload.');
            return;
        }

        const formData = new FormData();
        formData.append('document', file);
        formData.append('documentType', values.documentType);
        formData.append('label', values.label);
        formData.append('notes', values.notes);
        formData.append('relatedWarnings', JSON.stringify(values.relatedWarnings));

        setSaving(true);
        try {
            const response = await fetch(`${API_URL}/v1/seller/products/${productUuid}/compliance/documents`, {
                method: 'POST',
                headers: {},
                body: formData,
            });
            const json = await readJson(response);
            if (!response.ok) throw new Error(getErrorMessage(json, 'Could not upload document'));
            onUploaded(json.data);
            setValues({ documentType: 'other', label: '', notes: '', relatedWarnings: [] });
            setFile(null);
            event.target.reset();
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <form className="pc-upload-form" onSubmit={handleSubmit}>
            {error && <Alert type="error">{error}</Alert>}
            <div className="pc-form-grid">
                <div className="group">
                    <label htmlFor="pc-doc-type">Document Type</label>
                    <select
                        id="pc-doc-type"
                        value={values.documentType}
                        onChange={event => setValues(v => ({ ...v, documentType: event.target.value }))}
                    >
                        {documentTypes.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                    </select>
                </div>
                <div className="group">
                    <label htmlFor="pc-doc-file">File</label>
                    <input
                        id="pc-doc-file"
                        type="file"
                        onChange={event => setFile(event.target.files?.[0] || null)}
                    />
                </div>
            </div>
            <div className="group">
                <label htmlFor="pc-doc-label">Label</label>
                <input
                    id="pc-doc-label"
                    type="text"
                    value={values.label}
                    onChange={event => setValues(v => ({ ...v, label: event.target.value }))}
                    placeholder="Scene rider, consent check, location release..."
                />
            </div>
            <div className="group">
                <label>Restricted Content Covered</label>
                <div className="pc-checkbox-grid">
                    {policy.map(item => (
                        <label key={item.value} className="checkbox">
                            <input
                                type="checkbox"
                                checked={values.relatedWarnings.includes(item.value)}
                                onChange={() => toggleWarning(item.value)}
                            />
                            <span>{item.label}</span>
                        </label>
                    ))}
                </div>
            </div>
            <div className="group">
                <label htmlFor="pc-doc-notes">Notes</label>
                <textarea
                    id="pc-doc-notes"
                    rows="3"
                    value={values.notes}
                    onChange={event => setValues(v => ({ ...v, notes: event.target.value }))}
                    placeholder="Optional context for the reviewer"
                />
            </div>
            <div className="group">
                <button type="submit" className="button" disabled={saving}>
                    {saving ? 'Uploading...' : 'Upload Documentation'}
                </button>
            </div>
        </form>
    );
}

export default function ProductCompliancePage({ productUuid, defaultCustodian = '' }) {
    const { fetch, API_URL } = useAuth();
    const [loading, setLoading] = useState(true);
    const [product, setProduct] = useState(null);
    const [values, setValues] = useState({
        dateFilmed: '',
        locationFilmed: '',
        custodianOfRecords: defaultCustodian,
        contentWarnings: [],
    });
    const [documents, setDocuments] = useState([]);
    const [performers, setPerformers] = useState({
        productPerformers: [],
        channelPerformers: [],
        complianceInvitations: [],
    });
    const [policy, setPolicy] = useState(DEFAULT_RESTRICTED_TYPES);
    const [documentTypes, setDocumentTypes] = useState(DEFAULT_DOCUMENT_TYPES);
    const [missingRequirements, setMissingRequirements] = useState([]);
    const [mayhemCustodian, setMayhemCustodian] = useState('');
    const [selectedPolicy, setSelectedPolicy] = useState(null);
    const [existingPerformerUuid, setExistingPerformerUuid] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [performerMessage, setPerformerMessage] = useState('');
    const [performerError, setPerformerError] = useState('');
    const [reviewMessage, setReviewMessage] = useState('');
    const [reviewError, setReviewError] = useState('');
    const saveQueue = useRef(Promise.resolve());

    const selectedPolicies = useMemo(() => {
        return values.contentWarnings
            .map(warning => policy.find(item => item.value === warning))
            .filter(Boolean);
    }, [values.contentWarnings, policy]);

    const documentCoverage = useMemo(() => {
        return values.contentWarnings.reduce((out, warning) => {
            out[warning] = documents.some(doc => (doc.relatedWarnings || []).includes(warning));
            return out;
        }, {});
    }, [values.contentWarnings, documents]);

    useEffect(() => {
        loadAll();
    }, [productUuid]);

    const loadAll = async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        setError('');
        try {
            const [complianceResponse, performersResponse] = await Promise.all([
                fetch(`${API_URL}/v1/seller/products/${productUuid}/compliance`),
                fetch(`${API_URL}/v1/seller/products/${productUuid}/performers`),
            ]);
            const complianceJson = await readJson(complianceResponse);
            const performersJson = await readJson(performersResponse);
            if (!complianceResponse.ok) throw new Error(getErrorMessage(complianceJson, 'Could not load compliance'));
            if (!performersResponse.ok) throw new Error(getErrorMessage(performersJson, 'Could not load performers'));

            setProduct(complianceJson.data);
            setValues({
                dateFilmed: complianceJson.data.dateFilmed || '',
                locationFilmed: complianceJson.data.locationFilmed || '',
                custodianOfRecords: complianceJson.data.custodianOfRecords || '',
                contentWarnings: complianceJson.data.contentWarnings || [],
            });
            setDocuments(complianceJson.data.complianceDocuments || []);
            setPerformers({
                productPerformers: performersJson.data?.productPerformers || [],
                channelPerformers: performersJson.data?.channelPerformers || [],
                complianceInvitations: performersJson.data?.complianceInvitations || [],
            });
            setPolicy(complianceJson.meta?.restrictedContentTypes || DEFAULT_RESTRICTED_TYPES);
            setDocumentTypes(complianceJson.meta?.documentTypes || DEFAULT_DOCUMENT_TYPES);
            setMissingRequirements(complianceJson.meta?.missingReviewRequirements || []);
            setMayhemCustodian(complianceJson.meta?.mayhemCustodianOfRecords || '');
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const toggleWarning = (warning) => {
        setValues(prev => ({
            ...prev,
            contentWarnings: prev.contentWarnings.includes(warning)
                ? prev.contentWarnings.filter(item => item !== warning)
                : [...prev.contentWarnings, warning],
        }));
        setMessage('');
    };

    const requestComplianceSave = async ({quiet, payload}) => {
        setSaving(true);
        setError('');
        setMessage('');
        try {
            const response = await fetch(`${API_URL}/v1/seller/products/${productUuid}/compliance`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const json = await readJson(response);
            if (!response.ok) throw new Error(getErrorMessage(json, 'Could not save compliance'));
            setProduct(json.data);
            setDocuments(json.data.complianceDocuments || documents);
            setMissingRequirements(json.meta?.missingReviewRequirements || []);
            if (!quiet) {
                setMessage(json.meta?.warning || 'Compliance details saved.');
            }
            return json;
        } finally {
            setSaving(false);
        }
    };
    const saveCompliance = ({quiet = false, payload = values} = {}) => {
        const save = saveQueue.current
            .catch(() => undefined)
            .then(() => requestComplianceSave({quiet, payload}));
        saveQueue.current = save;
        return save;
    };
    const {cancelAutosave} = useDebouncedAutosave(
        values,
        async payload => {
            try {
                await saveCompliance({quiet: true, payload});
            } catch (saveError) {
                setError(saveError.message);
                throw saveError;
            }
        },
        {enabled: !loading && Boolean(productUuid)},
    );

    const handleSave = async () => {
        cancelAutosave();
        try {
            await saveCompliance();
        } catch (e) {
            setError(e.message);
        }
    };

    const handleAddPerformer = async (event) => {
        event.preventDefault();
        setPerformerMessage('');
        setPerformerError('');
        if (!existingPerformerUuid) {
            setPerformerError('Choose a performer to add.');
            return;
        }

        try {
            const response = await fetch(`${API_URL}/v1/seller/products/${productUuid}/performers`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ performerUuid: existingPerformerUuid }),
            });
            const json = await readJson(response);
            if (!response.ok) throw new Error(getErrorMessage(json, 'Could not add performer'));
            setExistingPerformerUuid('');
            setPerformerMessage('Performer added to this product.');
            await loadAll({ silent: true });
        } catch (e) {
            setPerformerError(e.message);
        }
    };

    const handleInvitePerformer = async (event) => {
        event.preventDefault();
        setPerformerMessage('');
        setPerformerError('');
        if (!inviteEmail) {
            setPerformerError('Enter an email address.');
            return;
        }

        try {
            const response = await fetch(`${API_URL}/v1/seller/products/${productUuid}/performers`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ performerEmail: inviteEmail }),
            });
            const json = await readJson(response);
            if (!response.ok) throw new Error(getErrorMessage(json, 'Could not invite performer'));
            setInviteEmail('');
            setPerformerMessage(json.meta?.message || 'Invitation sent.');
            await loadAll({ silent: true });
        } catch (e) {
            setPerformerError(e.message);
        }
    };

    const handleDocumentUploaded = (document) => {
        setDocuments(prev => [document, ...prev]);
        setMessage('Compliance document uploaded.');
        loadAll({ silent: true });
    };

    const handleDeleteDocument = async (documentUuid) => {
        setError('');
        setMessage('');
        try {
            const response = await fetch(`${API_URL}/v1/seller/products/${productUuid}/compliance/documents/${documentUuid}`, {
                method: 'DELETE',
            });
            const json = await readJson(response);
            if (!response.ok) throw new Error(getErrorMessage(json, 'Could not delete document'));
            setDocuments(prev => prev.filter(doc => doc.uuid !== documentUuid));
            setMissingRequirements(json.meta?.missingReviewRequirements || []);
            setMessage('Compliance document removed.');
        } catch (e) {
            setError(e.message);
        }
    };

    const handleViewDocument = async (documentUuid) => {
        setError('');
        try {
            const response = await fetch(`${API_URL}/v1/seller/products/${productUuid}/compliance/documents/${documentUuid}`);
            if (!response.ok) {
                const json = await readJson(response);
                throw new Error(getErrorMessage(json, 'Could not open document'));
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener');
            window.setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
        } catch (e) {
            setError(e.message);
        }
    };

    const handleRequestReview = async () => {
        cancelAutosave();
        setReviewError('');
        setReviewMessage('');
        try {
            await saveCompliance({ quiet: true });
            const response = await fetch(`${API_URL}/v1/seller/products/${productUuid}/compliance/request-review`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            const json = await readJson(response);
            if (!response.ok) throw new Error(getErrorMessage(json, 'Could not request review'));
            setProduct(prev => ({
                ...(prev || {}),
                requestedRedReviewAt: json.data?.requestedRedReviewAt || new Date().toISOString(),
            }));
            setMissingRequirements([]);
            setReviewMessage('Review request submitted. We will email you when review is complete.');
        } catch (e) {
            setReviewError(e.message);
            if (e.message.includes(':')) {
                setMissingRequirements(e.message.split(':').slice(1).join(':').split(';').map(item => item.trim()).filter(Boolean));
            }
        }
    };

    if (loading) {
        return (
            <Section title="Product Compliance" collapsible={false}>
                <p>Loading compliance details...</p>
            </Section>
        );
    }

    const hasPerformers = performers.productPerformers.length > 0 || performers.complianceInvitations.length > 0;
    const selectedWarningLabels = selectedPolicies.map(item => item.label);
    const hasMissingDocs = values.contentWarnings.some(warning => !documentCoverage[warning]);

    // Preview text for collapsible sections
    const detailsPreview = [
        values.dateFilmed && `Filmed ${values.dateFilmed}`,
        values.locationFilmed,
        values.custodianOfRecords && 'Custodian set',
        values.contentWarnings.length ? `${values.contentWarnings.length} restricted type${values.contentWarnings.length === 1 ? '' : 's'}` : 'No restricted types',
    ].filter(Boolean).join(' · ') || 'Production date, location, custodian, and content types.';

    const docsPreview = values.contentWarnings.length
        ? (hasMissingDocs ? 'Some required documentation is missing.' : 'All required documentation provided.')
        : 'No additional documentation required.';

    const uploadPreview = `${documents.length} document${documents.length === 1 ? '' : 's'} uploaded.`;

    const performersPreview = hasPerformers
        ? `${performers.productPerformers.length} performer${performers.productPerformers.length === 1 ? '' : 's'} added${performers.complianceInvitations.length ? `, ${performers.complianceInvitations.length} invited` : ''}.`
        : 'No performers added yet.';

    return (
        <div className="pc-page">
            {error && <Alert type="error">{error}</Alert>}
            {message && <Alert type={message.includes('reviewed') ? 'warning' : 'success'}>{message}</Alert>}

            <Section title="Review Readiness" collapsible={false} className="pc-section">
                <div className="pc-status-grid">
                    <div>
                        <strong>Product status</strong>
                        <span>{product?.isFlagged ? 'Needs review' : 'Approved or not flagged'}</span>
                    </div>
                    <div>
                        <strong>Review requested</strong>
                        <span>{product?.requestedRedReviewAt ? formatDateTime(product.requestedRedReviewAt) : 'Not submitted'}</span>
                    </div>
                    <div>
                        <strong>Restricted types</strong>
                        <span>{selectedWarningLabels.length ? selectedWarningLabels.join(', ') : 'None selected'}</span>
                    </div>
                    <div>
                        <strong>Performers</strong>
                        <span>{hasPerformers ? `${performers.productPerformers.length} added, ${performers.complianceInvitations.length} invited` : 'Missing'}</span>
                    </div>
                </div>
                {missingRequirements.length ? (
                    <Alert type="warning">
                        <strong>Before review:</strong>
                        <ul>
                            {missingRequirements.map(item => <li key={item}>{item}</li>)}
                        </ul>
                    </Alert>
                ) : (
                    <Alert type="success">This product has the minimum compliance information needed for review.</Alert>
                )}
            </Section>

            <Section title="Product Details" id="product-compliance-details" className="pc-section" preview={detailsPreview}>
                <div className="pc-form-grid">
                    <div className="group">
                        <label htmlFor="pc-date-filmed">Date Filmed / Produced</label>
                        <input
                            id="pc-date-filmed"
                            type="date"
                            value={values.dateFilmed}
                            onChange={event => setValues(v => ({ ...v, dateFilmed: event.target.value }))}
                        />
                        <p className="help">Used to verify each performer's age on the date of production.</p>
                    </div>
                    <div className="group">
                        <label htmlFor="pc-location-filmed">Location Filmed</label>
                        <input
                            id="pc-location-filmed"
                            type="text"
                            value={values.locationFilmed}
                            onChange={event => setValues(v => ({ ...v, locationFilmed: event.target.value }))}
                            placeholder="City, State, Country"
                        />
                        <p className="help">Use the jurisdiction where the content was produced.</p>
                    </div>
                </div>

                <div className="group">
                    <label htmlFor="pc-custodian">Custodian of Records - Physical Address Required</label>
                    <input
                        id="pc-custodian"
                        type="text"
                        value={values.custodianOfRecords}
                        onChange={event => setValues(v => ({ ...v, custodianOfRecords: event.target.value }))}
                        placeholder="Company/person, street address, city, state, postal code"
                    />
                    <p className="help">
                        28 CFR 75.6 requires a street/business address where records may be made available. A PO box does not satisfy the rule, and email alone is not enough. <a href="https://www.ecfr.gov/current/title-28/chapter-I/part-75/section-75.6" target="_blank" rel="noreferrer">Read 28 CFR 75.6</a>.
                    </p>
                    {mayhemCustodian && (
                        <button
                            type="button"
                            className="button secondary"
                            onClick={() => setValues(v => ({ ...v, custodianOfRecords: mayhemCustodian }))}
                        >
                            Use Mayhem Custodian
                        </button>
                    )}
                </div>

                <div className="group">
                    <label>Restricted Content Types</label>
                    <WarningChecklist
                        policy={policy}
                        selectedWarnings={values.contentWarnings}
                        documents={documents}
                        onToggle={toggleWarning}
                        onDetails={setSelectedPolicy}
                    />
                </div>

                <div className="group">
                    <button type="button" className="button" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Product Compliance'}
                    </button>
                </div>
            </Section>

            <Section title="Documentation Required" id="product-compliance-required-docs" className="pc-section" preview={docsPreview}>
                <DocumentsSummary
                    selectedWarnings={values.contentWarnings}
                    policy={policy}
                    documents={documents}
                />
                {hasMissingDocs && (
                    <Alert type="warning">Upload at least one supporting document for each selected restricted content type before requesting review.</Alert>
                )}
            </Section>

            <Section title="Additional Documentation" id="product-compliance-documents" className="pc-section" preview={uploadPreview}>
                <DocumentUploader
                    productUuid={productUuid}
                    policy={policy}
                    documentTypes={documentTypes}
                    onUploaded={handleDocumentUploaded}
                />

                <table className="pc-table">
                    <thead>
                        <tr>
                            <th>Document</th>
                            <th>Type</th>
                            <th>Covers</th>
                            <th>Uploaded</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {documents.map(doc => {
                            const docType = documentTypes.find(type => type.value === doc.documentType);
                            const covers = (doc.relatedWarnings || [])
                                .map(warning => policy.find(item => item.value === warning)?.label || warning);
                            return (
                                <tr key={doc.uuid}>
                                    <td data-label="Document">
                                        <strong>{doc.label || doc.file?.filename || 'Compliance document'}</strong>
                                        {doc.notes && <p className="help">{doc.notes}</p>}
                                    </td>
                                    <td data-label="Type">{docType?.label || doc.documentType || 'Other'}</td>
                                    <td data-label="Covers">{covers.length ? covers.join(', ') : '-'}</td>
                                    <td data-label="Uploaded">{formatDateTime(doc.createdAt)}</td>
                                    <td data-label="Actions">
                                        <div className="pc-action-row">
                                            <button type="button" className="button secondary" onClick={() => handleViewDocument(doc.uuid)}>
                                                View
                                            </button>
                                            <button type="button" className="button danger" onClick={() => handleDeleteDocument(doc.uuid)}>
                                                Remove
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {!documents.length && (
                            <tr>
                                <td colSpan={5}>No additional documentation uploaded yet.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </Section>

            <Section title="Performers" id="product-compliance-performers" className="pc-section" preview={performersPreview}>
                <PerformerTable performers={performers} dateFilmed={values.dateFilmed} />
                {performerError && <Alert type="error">{performerError}</Alert>}
                {performerMessage && <Alert type="success">{performerMessage}</Alert>}
                <div className="pc-two-column">
                    <form onSubmit={handleAddPerformer}>
                        <h3>Add Existing Performer</h3>
                        <div className="group">
                            <label htmlFor="pc-existing-performer">Performer</label>
                            <select
                                id="pc-existing-performer"
                                value={existingPerformerUuid}
                                onChange={event => setExistingPerformerUuid(event.target.value)}
                            >
                                <option value="">Select a performer</option>
                                {performers.channelPerformers.map(performer => (
                                    <option key={performer.uuid} value={performer.uuid}>{performer.name}</option>
                                ))}
                            </select>
                            <p className="help">This immediately links an already-reviewed performer to this product.</p>
                        </div>
                        <button type="submit" className="button">Add Performer</button>
                    </form>
                    <form onSubmit={handleInvitePerformer}>
                        <h3>Invite New Performer</h3>
                        <div className="group">
                            <label htmlFor="pc-invite-email">Email</label>
                            <input
                                id="pc-invite-email"
                                type="email"
                                value={inviteEmail}
                                onChange={event => setInviteEmail(event.target.value)}
                                placeholder="performer@example.com"
                            />
                            <p className="help">This sends a model-release and 2257 documentation request.</p>
                        </div>
                        <button type="submit" className="button">Send Invitation</button>
                    </form>
                </div>
            </Section>

            <Section title="Submit For Review" id="product-compliance-submit" className="pc-section" collapsible={false}>
                {reviewError && <Alert type="error">{reviewError}</Alert>}
                {reviewMessage && <Alert type="success">{reviewMessage}</Alert>}
                {product?.requestedRedReviewAt ? (
                    <p>Review requested at {formatDateTime(product.requestedRedReviewAt)}.</p>
                ) : (
                    <>
                        <p>Submitting saves the product compliance details first, then sends the RED review request.</p>
                        <button type="button" className="button" onClick={handleRequestReview} disabled={saving}>
                            Submit Product For Review
                        </button>
                    </>
                )}
            </Section>

            <Modal
                isOpen={!!selectedPolicy}
                onClose={() => setSelectedPolicy(null)}
                title={selectedPolicy?.label}
                size="medium"
                footer={(
                    <button type="button" className="button" onClick={() => setSelectedPolicy(null)}>
                        Close
                    </button>
                )}
            >
                {selectedPolicy && (
                    <div className="pc-policy-modal">
                        <p>{selectedPolicy.description}</p>
                        <h3>Required Safeguards</h3>
                        <ul>
                            {(selectedPolicy.requirements || []).map(item => <li key={item}>{item}</li>)}
                        </ul>
                        <h3>Documentation To Upload</h3>
                        <ul>
                            {(selectedPolicy.documentation || []).map(item => <li key={item}>{item}</li>)}
                        </ul>
                    </div>
                )}
            </Modal>
        </div>
    );
}
